import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { useProjectStore } from '../stores/projectStore';
import { useProjects } from '../hooks/useProjects';
import { EmptyState } from './EmptyState';
import { Button } from './Button';
import { LoadingSkeleton } from './LoadingSkeleton';
import type { PathType } from '../api/types';

// Wrapper for flat project-scoped routes (`/csdb`, `/import`, `/graphics`,
// `/applicability`, `/publishing`). The underlying page components read
// `:id` from the URL and have no useful behaviour when it is absent —
// previously, hitting one of these flat routes silently rendered a
// broken shell. This wrapper resolves the active project from the
// project-store + the projects list and either:
//   * redirects to `/projects/:id/<segment>` when the active project's
//     `pathType` matches the page's required pathType, OR
//   * renders an honest EmptyState with a CTA to /projects.
//
// The wrapper does NOT touch the per-project routes themselves — those
// still render the original page components directly.

export interface ProjectScopedRedirectProps {
  segment: string;
  label: string;
  pathType: PathType;
  icon?: ReactNode;
}

export function ProjectScopedRedirect({
  segment,
  label,
  pathType,
  icon,
}: ProjectScopedRedirectProps): JSX.Element {
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const projectsQ = useProjects('active');

  if (projectsQ.isLoading) {
    return <LoadingSkeleton variant="text" lines={6} />;
  }

  const active = projectsQ.data?.find((p) => p.id === activeProjectId) ?? null;

  if (active && active.pathType === pathType) {
    return <Navigate to={`/projects/${active.id}/${segment}`} replace />;
  }

  const pathTypeWord = pathType === 's1000d' ? 'S1000D' : 'HTML manual';
  const message = !active
    ? `${label} is project-scoped. Open an ${pathTypeWord} project from Home first.`
    : `${label} only applies to ${pathTypeWord} projects. The currently active project is a ${
        active.pathType === 's1000d' ? 'S1000D' : 'HTML manual'
      } project — switch to an ${pathTypeWord} project to access this page.`;

  return (
    <EmptyState
      icon={icon ?? <FolderOpen size={56} strokeWidth={1.5} aria-hidden="true" />}
      title={label}
      message={message}
      cta={
        <Link to="/projects">
          <Button>Go to Home</Button>
        </Link>
      }
    />
  );
}
